import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { X, MagnifyingGlass, User, UploadSimple, Shield, SignOut, Plus } from "@phosphor-icons/react";
import Logo from "./Logo";

/**
 * The phone menu. It is rendered straight into <body>: inside the navbar (which
 * has a blur effect) a "fixed" panel is sized to the 80px bar instead of the screen.
 */
export default function MobileMenu({ open, focusSearch, onClose, items, user, logout }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    if (focusSearch) setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, focusSearch, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const text = q.trim();
    if (!text) return;
    setQ("");
    onClose();
    navigate(`/search?q=${encodeURIComponent(text)}`);
  };
  const isCreator = user && (user.role === "creator" || user.role === "admin");

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#121212] flex flex-col"
      style={{ height: "100dvh", paddingTop: "env(safe-area-inset-top)" }}
      role="dialog"
      aria-modal="true"
      data-testid="mobile-menu"
    >
      <div className="flex items-center justify-between h-20 px-6 flex-shrink-0 border-b border-white/5">
        <Link to="/" onClick={onClose}><Logo /></Link>
        <button type="button" onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close" className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full active:bg-white/10">
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">
        <form onSubmit={submit} className="relative mb-6" data-testid="mobile-search-form">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search audios, scenepacks, creators…"
            enterKeyHint="search"
            autoComplete="off"
            data-testid="mobile-search-input"
            className="w-full h-12 pl-11 pr-4 rounded-full bg-[#1A1A1A] border border-white/10 focus:border-[#880015] focus:outline-none"
          />
        </form>

        <nav className="space-y-1" aria-label="Main">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onClose}
              className={({ isActive }) => `flex items-center justify-between font-display font-bold uppercase tracking-wider text-2xl py-3 border-b border-white/5 ${isActive ? "text-white" : "text-white/70"}`}
            >
              <span>
                {n.label}
                {n.soon && <span className="ml-3 align-middle text-[10px] tracking-[0.2em] px-2 py-1 rounded-full bg-gradient-to-r from-[#880015] to-[#C2102E] text-white">SOON</span>}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Link to="/requests?type=scenepack" onClick={onClose} className="flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider py-3 rounded-lg border border-white/10 text-white/60"><Plus size={12} /> Request scenepack</Link>
          <Link to="/requests?type=audio" onClick={onClose} className="flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase tracking-wider py-3 rounded-lg border border-white/10 text-white/60"><Plus size={12} /> Request audio</Link>
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-white/10 bg-[#161616] px-6 pt-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
        {user ? (
          <div data-testid="mobile-menu-user">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full bg-[#880015] flex items-center justify-center font-display font-bold">{user.name?.[0]?.toUpperCase() || "A"}</span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user.name}</div>
                <div className="text-[11px] font-mono text-white/40 truncate">{user.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/profile" onClick={onClose} className="flex items-center justify-center gap-2 py-3 rounded-lg border border-white/10 text-sm"><User size={15} /> Profile</Link>
              {isCreator && <Link to="/dashboard" onClick={onClose} className="flex items-center justify-center gap-2 py-3 rounded-lg border border-white/10 text-sm"><UploadSimple size={15} /> Dashboard</Link>}
              {user.role === "admin" && <Link to="/admin" onClick={onClose} className="flex items-center justify-center gap-2 py-3 rounded-lg border border-white/10 text-sm"><Shield size={15} /> Admin</Link>}
              <button type="button" onClick={() => { onClose(); logout(); }} data-testid="mobile-logout" className="flex items-center justify-center gap-2 py-3 rounded-lg border border-white/10 text-sm text-white/70"><SignOut size={15} /> Sign out</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="mobile-menu-auth">
            <Link to="/login" onClick={onClose} data-testid="mobile-signin" className="btn-secondary justify-center">Sign in</Link>
            <Link to="/register" onClick={onClose} data-testid="mobile-join" className="btn-primary justify-center">Join</Link>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
